import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import ReactLoading from "react-loading";
import profile from "../../../images/profileAvatar.jpg";

import { apiUrl, imageUrl as buildImageUrl } from '../../../config/api';
const EMPTY_AVERAGES = {
    mpg: 0,
    ppg: 0,
    rtpg: 0,
    apg: 0,
};

function normalizeText(value) {
    return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .toUpperCase();
}

function toNumber(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
}

function pickValue(source, keys, fallback = 0) {
    if (!source) return fallback;
    const keyList = Array.isArray(keys) ? keys : [keys];
    const foundKey = keyList.find((key) => source[key] !== undefined && source[key] !== null);
    return foundKey ? source[foundKey] : fallback;
}

function formatAverage(value) {
    return toNumber(value).toFixed(1);
}

function parseProfileResponse(payload) {
    if (typeof payload === "string") {
        try {
            return JSON.parse(payload);
        } catch (error) {
            return {};
        }
    }

    return payload || {};
}

function findLeagueAverage(averages, targetLeague) {
    const normalizedLeague = normalizeText(targetLeague);

    const exact = averages.find((item) => normalizeText(item?.liga) === normalizedLeague);
    if (exact) return exact;

    const targetYear = String(targetLeague || "").match(/\d{4}/)?.[0];
    if (!targetYear) return null;

    return averages.find((item) => String(item?.liga || "").includes(targetYear)) || null;
}

function buildTournamentAverages(averageItem) {
    if (!averageItem) return EMPTY_AVERAGES;

    return {
        mpg: pickValue(averageItem, ["mpg"]),
        ppg: pickValue(averageItem, ["ppg"]),
        rtpg: pickValue(averageItem, ["rtpg", "trpg"]),
        apg: pickValue(averageItem, ["apg", "astpg"]),
    };
}

export default function PartidoPlayerModal({ playerName, leagueName, isOpen, onClose, cacheRef }) {
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [displayName, setDisplayName] = useState("");
    const [averages, setAverages] = useState(EMPTY_AVERAGES);
    const [hasImageError, setHasImageError] = useState(false);

    const imageUrl = useMemo(
        () => buildImageUrl(playerName || ""),
        [playerName]
    );
    const resolvedImageSrc = hasImageError ? profile : imageUrl;

    useEffect(() => {
        if (!isOpen) return;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        window.addEventListener("keydown", onKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [isOpen, onClose]);

    useEffect(() => {
        setHasImageError(false);
    }, [playerName, isOpen]);

    useEffect(() => {
        if (!isOpen || !playerName) {
            setDisplayName("");
            setAverages(EMPTY_AVERAGES);
            setIsLoading(false);
            setIsError(false);
            return;
        }

        let isMounted = true;
        const cacheKey = `${playerName}::${leagueName || ""}`;
        const cache = cacheRef?.current;
        const cachedEntry = cache?.get(cacheKey);

        if (cachedEntry) {
            setDisplayName(cachedEntry.displayName);
            setAverages(cachedEntry.averages);
            setIsError(false);
            setIsLoading(false);
            return;
        }

        const fetchPlayerTournamentData = async () => {
            setIsLoading(true);
            setIsError(false);

            try {
                const response = await fetch(apiUrl(`perfil/${encodeURIComponent(playerName)}`));
                if (!response.ok) {
                    throw new Error(`Error! status: ${response.status}`);
                }

                const rawPayload = await response.json();
                const parsedPayload = parseProfileResponse(rawPayload);
                const profiles = Array.isArray(parsedPayload?.jugador) ? parsedPayload.jugador : [];
                const jugadorProfile = profiles.find((item) => item?.jugador) || profiles[0] || null;
                const torneoPromedios = Array.isArray(jugadorProfile?.torneosProm) ? jugadorProfile.torneosProm : [];
                const tournamentAverage = findLeagueAverage(torneoPromedios, leagueName);

                if (!isMounted) return;

                const resolvedDisplayName = jugadorProfile?.nombreAbrev || playerName;
                const resolvedAverages = buildTournamentAverages(tournamentAverage);

                setDisplayName(resolvedDisplayName);
                setAverages(resolvedAverages);
                cache?.set(cacheKey, { displayName: resolvedDisplayName, averages: resolvedAverages });
            } catch (error) {
                if (!isMounted) return;
                setIsError(true);
                setDisplayName(playerName);
                setAverages(EMPTY_AVERAGES);
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        fetchPlayerTournamentData();

        return () => {
            isMounted = false;
        };
    }, [isOpen, playerName, leagueName, cacheRef]);

    if (!isOpen || !playerName) return null;

    return (
        <AnimatePresence mode="wait">
            {isOpen && playerName && (
                <motion.div
                    className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    onClick={onClose}
                >
                    <motion.div
                        className="relative w-full max-w-md md:max-w-[590px] rounded-xl border border-white/20 bg-[#0b1a12] p-4 sm:p-6 shadow-2xl"
                        initial={{ scale: 0.95, opacity: 0, y: 10 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.95, opacity: 0, y: 10 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        onClick={(event) => event.stopPropagation()}
                    >
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute right-3 top-3 z-20 h-9 w-9 rounded-full border border-white/20 bg-black/30 text-white hover:bg-black/50"
                    aria-label="Cerrar modal"
                >
                    x
                </button>

                <div
                    className={`grid gap-4 md:justify-center md:gap-5 md:items-center ${isLoading ? "md:grid-cols-1 md:justify-items-center" : "md:grid-cols-[220px_minmax(0,300px)]"
                        }`}
                >
                    {!isLoading ? (
                        <div className="w-full max-w-[220px] md:max-w-none mx-auto rounded-xl border border-white/20 bg-black/20 p-2">
                            <img
                                src={resolvedImageSrc}
                                alt={`Foto de ${displayName || playerName}`}
                                className="w-full h-[260px] sm:h-[340px] object-cover rounded-lg"
                                loading="lazy"
                                onError={() => {
                                    setHasImageError(true);
                                }}
                            />
                        </div>
                    ) : null}

                    <div className={isLoading ? "w-full md:w-[300px]" : "w-full md:max-w-[300px]"}>
                        {isLoading ? (
                            <div
                                className="transition-all duration-700 ease-out min-h-[220px] md:min-h-[320px] flex flex-col items-center justify-center text-center"
                                style={{ opacity: isLoading ? 1 : 0, transform: isLoading ? "translateY(0)" : "translateY(2.5rem)" }}
                            >
                                <ReactLoading type="bars" color="#22c55e" width={70} className="mb-4" />
                                <p className="text-slate-200 text-lg font-semibold">Cargando datos del jugador...</p>
                            </div>
                        ) : (
                            <div className="transition-all duration-500 ease-out opacity-100 translate-y-0">
                                <h3 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-white mb-5 border-l-4 border-red-500 pl-3">
                                    {displayName || playerName}
                                </h3>
                                <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">{leagueName || "Liga"}</p>

                                {isError ? (
                                    <p className="text-red-300 mb-6">No se pudieron cargar los promedios del torneo.</p>
                                ) : (
                                    <div className="grid grid-cols-2 gap-2.5 mb-6 w-full">
                                        <div className="rounded-lg border border-white/10 bg-[#102216] px-2.5 py-2 text-center">
                                            <p className="text-[10px] uppercase tracking-wide text-slate-400">Min</p>
                                            <p className="text-lg font-bold text-white leading-tight">{formatAverage(averages.mpg)}</p>
                                        </div>
                                        <div className="rounded-lg border border-white/10 bg-[#102216] px-2.5 py-2 text-center">
                                            <p className="text-[10px] uppercase tracking-wide text-slate-400">Pts</p>
                                            <p className="text-lg font-bold text-white leading-tight">{formatAverage(averages.ppg)}</p>
                                        </div>
                                        <div className="rounded-lg border border-white/10 bg-[#102216] px-2.5 py-2 text-center">
                                            <p className="text-[10px] uppercase tracking-wide text-slate-400">Reb</p>
                                            <p className="text-lg font-bold text-white leading-tight">{formatAverage(averages.rtpg)}</p>
                                        </div>
                                        <div className="rounded-lg border border-white/10 bg-[#102216] px-2.5 py-2 text-center">
                                            <p className="text-[10px] uppercase tracking-wide text-slate-400">Ast</p>
                                            <p className="text-lg font-bold text-white leading-tight">{formatAverage(averages.apg)}</p>
                                        </div>
                                    </div>
                                )}

                                <Link
                                    to={`/jugador/${encodeURIComponent(playerName)}`}
                                    className="inline-flex items-center rounded-lg border border-green-500/40 bg-green-500/20 px-4 py-2 text-sm font-bold text-green-300 hover:bg-green-500/30 transition-colors"
                                    onClick={onClose}
                                >
                                    Ver perfil
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
